import React, { useState } from "react";
import QAButton from "../../../components/Design/Controls/QAButton";
import QAButtonRow from "../../../components/Design/Layout/QAButtonRow";
import SettingsTextField from "../../../components/Settings/Old/Form/SettingsTextField";
import { CreateScenarioCommandRole, RoleRO, UpdateScenarioCommandRole } from "../../../api/api";
import { makeStyles } from "@mui/styles";
import SettingsSelect from "../../../components/Settings/Old/Form/SettingsSelect";
import { Accordion, AccordionDetails, AccordionSummary, MenuItem } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import SettingsLabel from "../../../components/Settings/Old/Form/SettingsLabel";
import VariableForm from "../../../components/Variables/VariableForm";
import { VariableDefinition } from "../../../components/Variables/VariableDefinitionForm";
import TextInputDialog from "../../../components/Hooks/Dialog/TextInputDialog";
import useDialog from "../../../components/Hooks/Dialog/useDialog";
import EditGroupInTableDialog from "../Dialog/EditGroupInTableDialog";

interface Props {
    role: CreateScenarioCommandRole & UpdateScenarioCommandRole;
    onUpdate: (roleGroup: CreateScenarioCommandRole & UpdateScenarioCommandRole) => void;
    onDelete: () => void;
    availableRoles: RoleRO[];
    expanded: boolean;
    onExpandedChanged: (expanded: boolean) => void;
    disabled: boolean;
    scenarioVariables: VariableDefinition[];
}

const useStyles = makeStyles({
    roleRow: {
        width: "100%",
        backgroundColor: "#FFFFFF",
        boxShadow: "none",
        border: "1px solid #CCC",
        borderBottomWidth: "0px",
        "&:last-child": {
            borderBottomWidth: "1px"
        }
    },
    roleName: {
        flex: 1
    },
    roleRole: {
        flex: 1
    },
    title: {
        fontWeight: 500,
        width: "40%",
        flexShrink: 0
    },
    subtitle: {
        color: "rgba(0, 0, 0, 0.54)",
        flexGrow: 1
    },
    roleDetails: {
        gap: "0.5rem",
        display: "flex",
        flexDirection: "column"
    },
    roleInstances: {
        marginTop: "0.5rem",
        display: "flex",
        flexDirection: "column"
    },
    delete: {
        borderColor: "red",
        color: "red"
    }
});

const ScenarioRoleGroup: React.FC<Props> = props => {
    const classes = useStyles();
    const dialog = useDialog();

    const {
        disabled,
        role,
        availableRoles,
        onDelete,
        onUpdate,
        expanded,
        onExpandedChanged,
        scenarioVariables
    } = props;

    const [expandedInstance, setExpandedInstance] = useState<number>();
    const [editInTableOpen, setEditInTableOpen] = useState(false);

    return (
        <Accordion
            disableGutters
            expanded={expanded}
            onChange={(e, exp) => onExpandedChanged(exp)}
            className={classes.roleRow}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <span className={classes.title}>
                    Gruppe {role.name}
                </span>
                <span className={classes.subtitle}>
                    {role.instances.length} Instanz{role.instances.length === 1 ? "" : "en"} von Rolle {availableRoles.find(ar => ar.id === role.roleId)?.name}
                </span>
            </AccordionSummary>
            <AccordionDetails>

                <div className={classes.roleDetails}>
                    <SettingsTextField
                        label="Gruppenname"
                        value={role.name}
                        className={classes.roleName}
                        onChanged={value => onUpdate({
                            ...role,
                            name: value
                        })} />
                    <SettingsSelect
                        value={role.roleId}
                        className={classes.roleRole}
                        label="Rolle"
                        onChanged={value => onUpdate({
                            ...role,
                            roleId: value
                        })}>
                        {availableRoles.map(r => (
                            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        ))}
                    </SettingsSelect>
                </div>

                <div className={classes.roleInstances}>
                    <QAButtonRow marginBottom alignRight>

                        <QAButton
                            light
                            size="tiny"
                            className={classes.delete}
                            disabled={disabled}
                            title="Gruppe löschen"
                            onClicked={() => onDelete()} />

                        <QAButton
                            light
                            size="tiny"
                            disabled={disabled}
                            title="Instanz hinzufügen"
                            onClicked={() => onUpdate({
                                ...role,
                                instances: [
                                    ...role.instances,
                                    {
                                        name: (availableRoles.find(ar => ar.id === role.roleId)?.name ?? "Neue Instanz") + " " + (role.instances.length + 1),
                                        variables: {}
                                    }
                                ]
                            })} />

                        <QAButton
                            light
                            size="tiny"
                            disabled={disabled}
                            title="Mehrere Instanzen hinzufügen"
                            onClicked={async () => {
                                const response = await dialog.open(TextInputDialog, {
                                    title: "Mehrere Instanzen hinzufügen",
                                    question: "Wie viele Instanzen möchten Sie hinzufügen?",
                                    label: "Anzahl",
                                    acceptButtonTitle: "Hinzufügen"
                                });
                                if (!response.accepted) {
                                    return;
                                }

                                const count = parseInt(response.result);
                                if (isNaN(count) || count <= 0) {
                                    return;
                                }

                                const newInstances = [...role.instances];
                                for (let i = 0; i < count; i++) {
                                    newInstances.push({
                                        name: (availableRoles.find(ar => ar.id === role.roleId)?.name ?? "Neue Instanz") + " " + (newInstances.length + 1),
                                        variables: {}
                                    });
                                }
                                onUpdate({
                                    ...role,
                                    instances: newInstances
                                });
                            }} />

                        <QAButton
                            light
                            size="tiny"
                            disabled={disabled}
                            title="In Tabelle bearbeiten"
                            onClicked={() => setEditInTableOpen(true)} />

                    </QAButtonRow>

                    <div>
                        {role.instances.map((instance, instanceIndex) => (
                            <Accordion
                                key={instanceIndex}
                                disableGutters
                                expanded={expandedInstance === instanceIndex}
                                onChange={(e, exp) => setExpandedInstance(exp ? instanceIndex : undefined)}
                                className={classes.roleRow}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <span className={classes.title}>
                                        {instanceIndex + 1}. {instance.name || "Unbenannte Instanz"}
                                    </span>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.roleDetails}>

                                        <QAButtonRow marginBottom alignRight>
                                            <QAButton
                                                light
                                                size="tiny"
                                                className={classes.delete}
                                                disabled={disabled}
                                                title="Instanz löschen"
                                                onClicked={() => onUpdate({
                                                    ...role,
                                                    instances: role.instances.filter((_, i) => i !== instanceIndex)
                                                })} />
                                        </QAButtonRow>

                                        <SettingsTextField
                                            label="Name"
                                            value={instance.name ?? ""}
                                            className={classes.roleName}
                                            onChanged={value => onUpdate({
                                                ...role,
                                                instances: role.instances.map((ir, ii) => ii === instanceIndex ? {
                                                    ...ir,
                                                    name: value
                                                } : ir)
                                            })} />

                                        <SettingsLabel>Variablen</SettingsLabel>
                                        <VariableForm
                                            variables={availableRoles.find(ar => ar.id === role.roleId)?.variables ?? []}
                                            disabled={disabled}
                                            shadowedVariables={scenarioVariables}
                                            onValidate={console.log}
                                            onUpdate={(name, value) => onUpdate({
                                                ...role,
                                                instances: role.instances.map(i => i === instance ? {
                                                    ...i,
                                                    variables: {
                                                        ...i.variables,
                                                        [name]: value
                                                    }
                                                } : i)
                                            })}
                                            values={instance.variables} />

                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </div>
                </div>
            </AccordionDetails>

            <EditGroupInTableDialog
                open={editInTableOpen}
                role={role}
                onUpdate={onUpdate}
                availableRoles={availableRoles}
                disabled={disabled}
                scenarioVariables={scenarioVariables}
                onClose={() => setEditInTableOpen(false)} />
        </Accordion>
    );
};

export default ScenarioRoleGroup;
